import {
    select, take, put, takeLatest, call, actionChannel
} from 'redux-saga/effects';
import { RETRIEVED_BRAND_CONFIG, RETRIEVED_FACEPLATE_GROUPS } from 'capi/redux';

import getNowTimestamp from '../utils/time/getNowTimestamp';
import { handleSetDefaults } from '../catalogs/catalogSagas';
import { getSupportedCardTypes } from '../catalogs/catalogSelectors';
import { requestFaceplateGroups } from '../faceplate/faceplateModule';
import { getCurrencyCodes } from '../item/itemSelectors';
import {
    programLoaded,
    setProgramCode,
    programCleared,
    programChangeComplete,
    programConfigLoaded,
    CHANGE_PROGRAM,
    CLEAR_PROGRAM
} from './programModule';
import {
    getActiveProgramCode,
    getAllProgramCurrencies,
    isProgramConfigured
} from './programSelectors';
import {
    changeValue,
    fieldChangedAction,
    removeValue
} from '../item/newItemFormModule';
import { IS_PLASTIC_FIELD, newItemProgramCode, PROGRAM_CODE } from '../item/newItemForm';
import { getPreferredCurrencyCode } from '../routing/flowSelectors';
import { removeQueryParams, updateSearch } from '../routing/routing';


export function* rejectProgramCode() {
    yield call(programChange, null);
}

export function* isProgramCodeValid(programCode) {
    const programIsConfigured = (yield select(isProgramConfigured))(programCode);
    if (!programIsConfigured) {
        return false;
    }
    const cartCurrencies = yield select(getCurrencyCodes);
    const programCurrencies = (yield select(getAllProgramCurrencies))(programCode);
    if (!programCurrencies.isSuperset(cartCurrencies)) {
        return false;
    }
    
    const preferredCurrencyCode = yield select(getPreferredCurrencyCode);
    if (preferredCurrencyCode && !programCurrencies.includes(preferredCurrencyCode)) {
        return false;
    }
    return true;
}


export function* validateActiveProgramCode() {
    const activeProgramCode = yield select(getActiveProgramCode);
    if (activeProgramCode) {
        const activeProgramIsValid = yield call(isProgramCodeValid, activeProgramCode);
        if (!activeProgramIsValid) {
            yield call(rejectProgramCode);
        } else {
            const newItemProgramCodeValue = yield select(newItemProgramCode);
            if (activeProgramCode !== newItemProgramCodeValue) {
                yield call(programDataChange, activeProgramCode);
            }
        }
    } else {
        yield put(removeValue(PROGRAM_CODE));
    }
    
    yield put(programLoaded());
}

const IS_PLASTIC_FIELD_CHANGED = fieldChangedAction(IS_PLASTIC_FIELD);


export function* programChange(programCode) {
    yield put(setProgramCode(programCode));

    if (programCode) {
        yield call(updateSearch, { programCode });
    } else {
        yield call(removeQueryParams, ['programCode']);
    }

    
    const faceplatesRetrievedChannel = yield actionChannel(RETRIEVED_FACEPLATE_GROUPS);
    yield put(requestFaceplateGroups(programCode));
    yield take(faceplatesRetrievedChannel);
    faceplatesRetrievedChannel.close();

    yield call(programDataChange, programCode);
}


export function* programDataChange(programCode) {
    const programSupportedCardTypes = yield select(getSupportedCardTypes);
    const supportsPlastic = programSupportedCardTypes.get('supportsPlasticCards');
    const supportsDigital = programSupportedCardTypes.get('supportsDigitalCards');

    
    
    const plasticChangeChannel = yield actionChannel(IS_PLASTIC_FIELD_CHANGED);
    yield put(changeValue(
        IS_PLASTIC_FIELD,
        (supportsPlastic && !supportsDigital)
    ));
    if (programCode) {
        yield put(changeValue(PROGRAM_CODE, programCode));
    } else {
        yield put(removeValue(PROGRAM_CODE));
    }

    yield take(plasticChangeChannel);
    plasticChangeChannel.close();

    yield call(handleSetDefaults);
    yield put(programChangeComplete());
}


export function* validateAndChangeProgram({ programCode }) {
    const programIsValid = yield call(isProgramCodeValid, programCode);
    yield put(programLoaded());
    if (programIsValid) {
        yield call(programChange, programCode);
    } else {
        
        
        yield call(rejectProgramCode, programCode);
    }
}


export function* clearProgram() {
    const activeProgramCode = yield select(getActiveProgramCode);
    if (activeProgramCode) {
        yield call(validateAndChangeProgram, { programCode: null });
    }
    yield put(programCleared());
}


export function* programSagasWatcher() {
    yield takeLatest(RETRIEVED_BRAND_CONFIG.PROGRAMS, function* initializePrograms() {
        yield call(validateActiveProgramCode);
        
        
        
        const now = yield call(getNowTimestamp);
        yield put(programConfigLoaded(now));
    });
    yield takeLatest(CLEAR_PROGRAM, clearProgram);
    yield takeLatest(CHANGE_PROGRAM, validateAndChangeProgram);
}
